import React, { useState } from 'react';
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Switch,
    FormControlLabel,
    Typography,
} from '@material-ui/core';
import { useQuery, DateField, useUpdate, useNotify } from 'react-admin';
import CountryField from '../../components/CountryField';
import UserReferenceField from '../../components/UserReferenceField';
import ItemStats from '../../components/ItemStats';
import LinkToRelatedItems from './LinkToRelatedItems';
import { StorageIcon } from '../../storages';
import { RequestIcon } from '../../requests';
import { OrderIcon } from '../../orders';
import { CardIcon } from '../../cards';
import { ChargeIcon } from '../../charges';
import { PaymentIcon } from '../../payments';
import BillingCard from '../../components/BillingCard';

export const PartnerSummary = props => {
    const { record, permissions = [] } = props;
    const { data } = useQuery({
        type: 'getOne',
        resource: 'partner-stats',
        payload: { id: record.id },
    });
    var partnerStats = data;

    // State for the postpaid invoice toggle switch
    const [isPostpaidInvoiceEnabled, setIsPostpaidInvoiceEnabled] = useState(
        record.isPostpaidInvoiceEnabled || false
    );

    // State for the show CBM in charge description toggle
    const [
        showCbmInChargeDescription,
        setShowCbmInChargeDescription,
    ] = useState(record.showCbmInChargeDescription || false);

    const notify = useNotify();

    // useUpdate hook to make the PATCH request for postpaid invoice
    const [updatePartner, { loading }] = useUpdate(
        'partners',
        record.id,
        { isPostpaidInvoiceEnabled: !isPostpaidInvoiceEnabled },
        record,
        {
            undoable: false,
            onSuccess: () => {
                setIsPostpaidInvoiceEnabled(!isPostpaidInvoiceEnabled);
                notify('Postpaid invoice setting updated', 'info');
            },
            onFailure: error => {
                notify(
                    `Error: ${error.message || 'Failed to update setting'}`,
                    'warning'
                );
            },
        }
    );

    // useUpdate hook to make the PATCH request for CBM toggle
    const [updatePartnerCbm, { loading: updateCbmLoading }] = useUpdate(
        'partners',
        record.id,
        { showCbmInChargeDescription: !showCbmInChargeDescription },
        record,
        {
            undoable: false,
            onSuccess: () => {
                setShowCbmInChargeDescription(!showCbmInChargeDescription);
                notify('CBM in charge description setting updated', 'info');
            },
            onFailure: error => {
                notify(
                    `Error: ${error.message || 'Failed to update CBM setting'}`,
                    'warning'
                );
            },
        }
    );

    // Handle toggle change for postpaid invoice
    const handleToggleChange = () => {
        updatePartner();
    };

    // Handle toggle change for CBM setting
    const handleCbmToggleChange = () => {
        updatePartnerCbm();
    };
    return (
        <div>
            <Table>
                <TableBody>
                    <TableRow>
                        <TableCell>
                            <label>ID</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            {record.id}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <label>Name</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            {record.name}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <label>Country</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            <CountryField record={record} />
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <label>Logo</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            {record.logoUrl}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            <label>Created On</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            <DateField record={record} source="createdAt" />
                        </TableCell>
                    </TableRow>
                    {record && record.id !== 1 && (
                        <TableRow>
                            <TableCell>
                                <label>Post paid Invoice status</label>
                            </TableCell>
                            <TableCell style={{ textAlign: 'right' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isPostpaidInvoiceEnabled}
                                            onChange={handleToggleChange}
                                            color="primary"
                                            disabled={loading}
                                        />
                                    }
                                    label={
                                        isPostpaidInvoiceEnabled
                                            ? 'Enabled'
                                            : 'Disabled'
                                    }
                                    labelPlacement="start"
                                />
                            </TableCell>
                        </TableRow>
                    )}
                    {/* Show the CBM in charge description toggle row */}
                    <TableRow>
                        <TableCell>
                            <label>Show CBM in Charge Description</label>
                        </TableCell>
                        <TableCell style={{ textAlign: 'right' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showCbmInChargeDescription}
                                        onChange={handleCbmToggleChange}
                                        color="primary"
                                        disabled={updateCbmLoading}
                                    />
                                }
                                label={
                                    showCbmInChargeDescription
                                        ? 'Enabled'
                                        : 'Disabled'
                                }
                                labelPlacement="start"
                            />
                        </TableCell>
                    </TableRow>
                </TableBody>
                <TableRow>
                    <TableCell>Admin</TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                        <UserReferenceField {...props} label="Admin" />
                    </TableCell>
                </TableRow>
            </Table>
            <br />
            <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                <Grid container spacing={3}>
                    {permissions.indexOf('partner') === -1 && (
                        <Grid item xs={6} sm={3}>
                            <BillingCard
                                billing={partnerStats && partnerStats.billing}
                            />
                        </Grid>
                    )}
                    <Grid item xs={6} sm={3}>
                        <LinkToRelatedItems
                            partnerId={record.id}
                            resource="storages"
                            icon={<StorageIcon />}
                        >
                            {partnerStats && partnerStats.storages && (
                                <ItemStats
                                    data={[
                                        ...partnerStats.storages,
                                        ...((!!partnerStats.size && [
                                            {
                                                status: 'Volume stored',
                                                count: `${partnerStats.size} CBM`,
                                            },
                                        ]) ||
                                            []),
                                    ]}
                                />
                            )}
                        </LinkToRelatedItems>
                    </Grid>

                    {permissions.indexOf('partner') === -1 && (
                        <Grid item xs={6} sm={3}>
                            <LinkToRelatedItems
                                partnerId={record.id}
                                resource="requests"
                                icon={<RequestIcon />}
                            >
                                {partnerStats && partnerStats.requests && (
                                    <ItemStats data={partnerStats.requests} />
                                )}
                            </LinkToRelatedItems>
                        </Grid>
                    )}

                    <Grid item xs={6} sm={3}>
                        <LinkToRelatedItems
                            partnerId={record.id}
                            resource="orders"
                            icon={<OrderIcon />}
                        >
                            {partnerStats && partnerStats.orders && (
                                <ItemStats data={partnerStats.orders} />
                            )}
                        </LinkToRelatedItems>
                    </Grid>

                    {permissions.indexOf('partner') === -1 && (
                        <Grid item xs={6} sm={3}>
                            <LinkToRelatedItems
                                partnerId={record.id}
                                resource="charges"
                                icon={<ChargeIcon />}
                            >
                                {partnerStats && partnerStats.charges && (
                                    <ItemStats
                                        source="category"
                                        unit="currency"
                                        data={partnerStats.charges}
                                    />
                                )}
                            </LinkToRelatedItems>
                        </Grid>
                    )}

                    {permissions.indexOf('partner') === -1 && (
                        <Grid item xs={6} sm={3}>
                            <LinkToRelatedItems
                                partnerId={record.id}
                                resource="payments"
                                icon={<PaymentIcon />}
                            >
                                {partnerStats && partnerStats.payments && (
                                    <ItemStats data={partnerStats.payments} />
                                )}
                            </LinkToRelatedItems>
                        </Grid>
                    )}

                    {permissions.indexOf('partner') === -1 && (
                        <Grid item xs={6} sm={3}>
                            <LinkToRelatedItems
                                partnerId={record.id}
                                resource="cards"
                                icon={<CardIcon />}
                            >
                                {partnerStats && partnerStats.cards && (
                                    <ItemStats data={partnerStats.cards} />
                                )}
                            </LinkToRelatedItems>
                        </Grid>
                    )}
                </Grid>
            </div>
        </div>
    );
};
